/**
 * Open-jaw trip matching — pair two one-way deals into a multi-city itinerary.
 * Fly into city A → overland travel → fly home from city B.
 * Pure static logic, no API calls, no server imports.
 */

// Airport proximity map: destinations reachable by train/bus/short domestic flight
export const NEARBY_AIRPORTS = {
  // Europe
  LIS: ['OPO', 'FAO'],
  OPO: ['LIS', 'FAO'],
  FAO: ['LIS', 'OPO'],
  BCN: ['MAD', 'VLC', 'SVQ'],
  MAD: ['BCN', 'VLC', 'SVQ'],
  VLC: ['BCN', 'MAD'],
  SVQ: ['BCN', 'MAD'],
  FCO: ['NAP', 'MXP', 'FLR', 'VCE'],
  NAP: ['FCO', 'MXP'],
  MXP: ['FCO', 'NAP', 'VCE'],
  VCE: ['FCO', 'MXP', 'FLR'],
  FLR: ['FCO', 'VCE'],
  ATH: ['SKG'],
  SKG: ['ATH'],
  PRG: ['VIE', 'BUD'],
  VIE: ['PRG', 'BUD'],
  BUD: ['VIE', 'PRG'],
  WAW: ['KRK', 'GDN'],
  KRK: ['WAW'],
  // Southeast Asia
  BKK: ['HKT', 'SIN', 'KUL', 'DPS'],
  HKT: ['BKK', 'SIN', 'KUL'],
  SIN: ['KUL', 'BKK', 'DPS'],
  KUL: ['SIN', 'BKK', 'DPS'],
  DPS: ['SIN', 'KUL', 'BKK'],
  // East Asia
  NRT: ['ICN', 'HND'],
  HND: ['NRT', 'ICN'],
  ICN: ['NRT', 'HND'],
  HKG: ['MFM', 'SZX'],
  // Americas
  JFK: ['EWR', 'MIA', 'LAX', 'SFO'],
  EWR: ['JFK', 'MIA'],
  MIA: ['JFK', 'LAX'],
  LAX: ['SFO', 'JFK', 'MIA'],
  SFO: ['LAX', 'JFK'],
  // Oceania
  SYD: ['MEL', 'AKL'],
  MEL: ['SYD', 'AKL'],
  AKL: ['SYD', 'MEL'],
  // Middle East
  DXB: ['DOH', 'AUH'],
  DOH: ['DXB', 'AUH'],
  AUH: ['DXB', 'DOH'],
  // Africa
  JNB: ['CPT'],
  CPT: ['JNB'],
}

// Static overland hints between paired cities
const TRANSPORT_HINTS = {
  'LIS-OPO': 'train (~3h)', 'OPO-LIS': 'train (~3h)',
  'BCN-MAD': 'AVE train (~2.5h)', 'MAD-BCN': 'AVE train (~2.5h)',
  'BCN-VLC': 'train (~3h)', 'VLC-BCN': 'train (~3h)',
  'MAD-VLC': 'train (~1.5h)', 'VLC-MAD': 'train (~1.5h)',
  'FCO-NAP': 'train (~1.2h)', 'NAP-FCO': 'train (~1.2h)',
  'FCO-VCE': 'train (~3.5h)', 'VCE-FCO': 'train (~3.5h)',
  'MXP-VCE': 'train (~2.5h)', 'VCE-MXP': 'train (~2.5h)',
  'FCO-FLR': 'train (~1.5h)', 'FLR-FCO': 'train (~1.5h)',
  'ATH-SKG': 'bus/train (~5h)', 'SKG-ATH': 'bus/train (~5h)',
  'PRG-VIE': 'train (~4h)', 'VIE-PRG': 'train (~4h)',
  'VIE-BUD': 'train (~2.5h)', 'BUD-VIE': 'train (~2.5h)',
  'PRG-BUD': 'bus (~6h)', 'BUD-PRG': 'bus (~6h)',
  'BKK-HKT': 'flight (~1.5h)', 'HKT-BKK': 'flight (~1.5h)',
  'BKK-SIN': 'flight (~2.5h)', 'SIN-BKK': 'flight (~2.5h)',
  'BKK-DPS': 'flight (~5.5h)', 'DPS-BKK': 'flight (~5.5h)',
  'BKK-KUL': 'flight (~2h)', 'KUL-BKK': 'flight (~2h)',
  'SIN-KUL': 'bus (~5h) or flight (~1h)', 'KUL-SIN': 'bus (~5h) or flight (~1h)',
  'SIN-DPS': 'flight (~2.5h)', 'DPS-SIN': 'flight (~2.5h)',
  'KUL-DPS': 'flight (~2.5h)', 'DPS-KUL': 'flight (~2.5h)',
  'NRT-ICN': 'flight (~2.5h)', 'ICN-NRT': 'flight (~2.5h)',
  'NRT-HND': 'train (~35min)', 'HND-NRT': 'train (~35min)',
  'JFK-MIA': 'flight (~3h)', 'MIA-JFK': 'flight (~3h)',
  'JFK-LAX': 'flight (~5.5h)', 'LAX-JFK': 'flight (~5.5h)',
  'LAX-SFO': 'drive (~6h) or flight (~1.5h)', 'SFO-LAX': 'drive (~6h) or flight (~1.5h)',
  'SYD-MEL': 'flight (~1.5h)', 'MEL-SYD': 'flight (~1.5h)',
  'SYD-AKL': 'flight (~3h)', 'AKL-SYD': 'flight (~3h)',
  'DXB-DOH': 'flight (~1.5h)', 'DOH-DXB': 'flight (~1.5h)',
  'DXB-AUH': 'drive (~1.5h)', 'AUH-DXB': 'drive (~1.5h)',
  'JNB-CPT': 'flight (~2h)', 'CPT-JNB': 'flight (~2h)',
}

/**
 * Find open-jaw trip pairs from a list of deals.
 *
 * Matching criteria:
 * - Same origin airport for both legs
 * - Destination of leg A is near destination of leg B (per NEARBY_AIRPORTS)
 * - Neither deal is expired
 *
 * @param {Array}  deals
 * @param {Object} [opts]
 * @param {string} [opts.filterDealId]  - only return pairs that include this deal ID
 * @param {string} [opts.filterOrigin]  - only return pairs from this departure airport
 * @returns {Array} sorted by combinedPrice asc: [{ dealA, dealB, combinedPrice, transportHint }]
 */
export function findOpenJawPairs(deals, opts = {}) {
  const { filterDealId = null, filterOrigin = null } = opts
  const now = Date.now()
  const active = deals.filter((d) => !d.expired_at || new Date(d.expired_at).getTime() > now)

  const pairs = []
  const seen = new Set()

  for (const dealA of active) {
    const nearby = NEARBY_AIRPORTS[dealA.destination]
    if (!nearby) continue
    if (filterOrigin && dealA.origin !== filterOrigin) continue

    for (const dealB of active) {
      if (dealA.id === dealB.id) continue
      if (!nearby.includes(dealB.destination)) continue
      if (dealA.origin !== dealB.origin) continue

      const pairKey = [dealA.id, dealB.id].sort().join('|')
      if (seen.has(pairKey)) continue
      seen.add(pairKey)

      if (filterDealId && dealA.id !== filterDealId && dealB.id !== filterDealId) continue

      const combinedPrice = dealA.price + dealB.price
      const transportHint =
        TRANSPORT_HINTS[`${dealA.destination}-${dealB.destination}`] ||
        TRANSPORT_HINTS[`${dealB.destination}-${dealA.destination}`] ||
        'overland travel'

      pairs.push({ dealA, dealB, combinedPrice, transportHint })
    }
  }

  return pairs.sort((a, b) => a.combinedPrice - b.combinedPrice)
}
